<div
  *ngIf="open()"
  class="fixed inset-0 z-[1000] flex items-center justify-center modal-overlay"
  role="dialog"
  aria-modal="true"
  [attr.aria-labelledby]="'user-manual-title'"
  (click)="onBackdrop($event)"
>
  <!-- Backdrop -->
  <div class="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>

  <!-- Image-only view (just the image + close icon) -->
  <div
    *ngIf="options.imageOnly"
    class="um-image-only relative"
    (click)="$event.stopPropagation()"
  >
    <button
      (click)="close()"
      class="um-image-only-close"
      aria-label="Close"
    >
      <svg viewBox="0 0 24 24" class="h-5 w-5">
        <path
          d="M6 6l12 12M18 6L6 18"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <img
      [src]="
        steps()[currentIndex()].imageUrl ||
        steps()[currentIndex()].imageUrlLg ||
        steps()[currentIndex()].imageUrlSm
      "
      [alt]="
        steps()[currentIndex()].alt ||
        steps()[currentIndex()].title ||
        'Guide image'
      "
      class="um-image-only-img"
      loading="eager"
    />
  </div>

  <!-- Modal Card -->
  <div
    *ngIf="!options.imageOnly"
    class="um-card relative w-[92vw] max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col slide-up"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="um-header flex items-center justify-between gap-3 px-5 py-4">
      <div class="flex items-center gap-3 min-w-0">
        <div class="um-badge h-9 w-9 rounded-xl flex items-center justify-center shrink-0">
          <!-- camera icon -->
          <svg viewBox="0 0 24 24" class="h-5 w-5">
            <path
              d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z"
              fill="currentColor"
              opacity=".2"
            />
            <path d="M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" fill="currentColor" />
          </svg>
        </div>
        <div class="min-w-0">
          <h2
            id="user-manual-title"
            class="text-base md:text-lg font-semibold tracking-tight text-white truncate"
          >
            {{ options.title }}
          </h2>
          <p class="um-subtle text-xs">
            Step {{ currentIndex() + 1 }} of {{ steps().length }}
          </p>
        </div>
      </div>

      <button
        *ngIf="options.closable"
        (click)="close()"
        class="um-icon-btn rounded-xl p-2 shrink-0"
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" class="h-5 w-5">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <!-- Content -->
    <div class="um-content flex-1 flex flex-col min-h-0 overflow-y-auto">
      <!-- Description -->
      <div
        *ngIf="steps()[currentIndex()].description"
        class="px-5 md:px-6 pt-4"
      >
        <p class="um-desc text-xs md:text-sm whitespace-pre-line text-center mx-auto max-w-[85%]">
          {{ steps()[currentIndex()].description }}
        </p>
      </div>

      <!-- Image / Stage -->
      <div class="relative flex-1 flex items-center justify-center px-12 md:px-16 py-5 min-h-[240px]">
        <!-- Prev -->
        <button
          (click)="prev()"
          [disabled]="currentIndex() === 0"
          class="um-nav absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-11 w-11 md:h-12 md:w-12 rounded-full z-10"
          aria-label="Previous step"
        >
          <svg viewBox="0 0 24 24" class="mx-auto h-6 w-6">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <ng-container *ngFor="let s of steps(); index as i">
          <div
            *ngIf="i === currentIndex()"
            [@crossSlide]="{ value: i, params: animParams }"
            class="w-full flex items-center justify-center"
          >
            <picture class="block flex items-center justify-center">
              <!-- Large/Desktop -->
              <source
                *ngIf="steps()[currentIndex()].imageUrlLg"
                media="(min-width: 768px)"
                [srcset]="steps()[currentIndex()].imageUrlLg"
              />

              <!-- Small/Mobile -->
              <source
                *ngIf="steps()[currentIndex()].imageUrlSm"
                media="(max-width: 767.98px)"
                [srcset]="steps()[currentIndex()].imageUrlSm"
              />

              <!-- Fallback -->
              <img
                [src]="
                  steps()[currentIndex()].imageUrl ||
                  steps()[currentIndex()].imageUrlLg ||
                  steps()[currentIndex()].imageUrlSm
                "
                [alt]="
                  steps()[currentIndex()].alt ||
                  steps()[currentIndex()].title ||
                  'Guide image'
                "
                class="um-img max-h-[45vh] max-w-full w-auto rounded-xl object-contain"
                (load)="imageLoaded = true"
                (error)="imageLoaded = true"
                loading="eager"
              />
            </picture>
          </div>
        </ng-container>

        <div
          *ngIf="!imageLoaded"
          class="um-skeleton h-56 w-72 md:h-80 md:w-[480px] rounded-xl animate-pulse"
        ></div>

        <!-- Next -->
        <button
          (click)="next()"
          [disabled]="currentIndex() === steps().length - 1"
          class="um-nav absolute right-3 md:right-4 top-1/2 -translate-y-1/2 h-11 w-11 md:h-12 md:w-12 rounded-full z-10"
          aria-label="Next step"
        >
          <svg viewBox="0 0 24 24" class="mx-auto h-6 w-6">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Progress bar -->
    <div class="um-progress-track relative h-1">
      <div class="um-progress-fill h-1 transition-[width]" [style.width.%]="progress()"></div>
    </div>

    <!-- Footer -->
    <div class="um-footer flex items-center justify-between gap-3 px-5 py-4">
      <div class="um-subtle hidden md:block text-xs">
        Tip: Use ← and → keys. Press ESC to close.
      </div>
      <div class="flex items-center gap-2 ml-auto">
        <button
          (click)="prev()"
          [disabled]="currentIndex() === 0"
          class="um-btn-ghost px-4 py-2 rounded-xl text-sm"
        >
          Back
        </button>
        <button
          *ngIf="currentIndex() < steps().length - 1"
          (click)="next()"
          class="um-btn-primary px-5 py-2 rounded-xl text-sm"
        >
          Next
        </button>
        <button
          *ngIf="currentIndex() === steps().length - 1"
          (click)="close()"
          class="um-btn-primary px-5 py-2 rounded-xl text-sm"
        >
          Finish
        </button>
      </div>
    </div>
  </div>
</div>
